import { ImFilePdf } from "react-icons/im";

class ResourceLoader {
  constructor() {
    this.resources = new Map();
    this.loadedCount = 0;
    this.completedCount = 0;
    this.totalCount = 0;
    this.loadingPromise = null;
    this.hasStartedLoading = false;
    this.progressListeners = new Set();
  }


  registerResources(resourceList) {

    this.resources.clear();
    this.loadedCount = 0;
    this.completedCount = 0;
    this.hasStartedLoading = false;

    if (!resourceList || resourceList.length === 0) {
      this.totalCount = 0;
      this.hasStartedLoading = true;

      this._notifyProgressListeners();
      return;
    }

    this.totalCount = resourceList.length;

    resourceList.forEach((resource) => {
      this.resources.set(resource.id, {
        ...resource,
        loaded: false,
        element: null,
      });
    });


    this._notifyProgressListeners();
  }


  addProgressListener(listener) {
    this.progressListeners.add(listener);
  }

  // 移除进度监听器
  removeProgressListener(listener) {
    this.progressListeners.delete(listener);
  }

  // 通知所有进度监听器
  _notifyProgressListeners() {
    const progress = this.getProgress();
    this.progressListeners.forEach((listener) => listener(progress));
  }

  // 预加载图片
  loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.referrerPolicy = "no-referrer";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  // 预加载字体
  loadFont(fontName, fontUrl) {
    return new Promise((resolve, reject) => {
      const font = new FontFace(fontName, `url(${fontUrl})`);

      font
        .load()
        .then((loadedFont) => {
          document.fonts.add(loadedFont);
          resolve(loadedFont);
        })
        .catch(() => {
          // 尝试使用 fetch 加载
          fetch(fontUrl, {
            referrerPolicy: "no-referrer",
            mode: "cors",
          })
            .then((response) => response.blob())
            .then((blob) => {
              const fontFace = new FontFace(
                fontName,
                `url(${URL.createObjectURL(blob)})`,
              );
              return fontFace.load();
            })
            .then((loadedFont) => {
              document.fonts.add(loadedFont);
              resolve(loadedFont);
            })
            .catch(reject);
        });
    });
  }
  loadFile(url) {
    return fetch(url, {
      referrerPolicy: "no-referrer",
      mode: "cors",
    });
  }

  // 批量预加载
  async preloadAll() {
    // 防止重复调用
    if (this.hasStartedLoading) {
      return this.loadingPromise;
    }

    this.hasStartedLoading = true;

    // 如果没有资源需要加载，直接完成并通知100%
    if (this.totalCount === 0) {
      this.loadingPromise = Promise.resolve();
      this._notifyProgressListeners(); // 确保100%
      return this.loadingPromise;
    }

    const promises = [];

    for (const [id, resource] of this.resources) {
      let promise;

      switch (resource.type) {
        case "image":
          promise = this.loadImage(resource.url)
            .then((element) => {
              this.resources.get(id).element = element;
              this.resources.get(id).loaded = true;
              this.loadedCount++;
              this.completedCount++;
              this._notifyProgressListeners();
            })
            .catch(() => {
              this.resources.get(id).loaded = false;
              this.completedCount++;
              this._notifyProgressListeners();
            });
          break;

        case "font":
          promise = this.loadFont(resource.name || id, resource.url)
            .then(() => {
              this.resources.get(id).loaded = true;
              this.loadedCount++;
              this.completedCount++;
              this._notifyProgressListeners();
            })
            .catch(() => {
              this.resources.get(id).loaded = false;
              this.completedCount++;
              this._notifyProgressListeners();
            });
          break;

        case "css":
          promise = new Promise((resolve) => {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = resource.url;
            link.referrerPolicy = "no-referrer";
            link.onload = () => {
              this.resources.get(id).loaded = true;
              this.loadedCount++;
              this.completedCount++;
              this._notifyProgressListeners();
              resolve();
            };
            link.onerror = () => {
              this.resources.get(id).loaded = false;
              this.completedCount++;
              this._notifyProgressListeners();
              resolve();
            };
            document.head.appendChild(link);
          });
          break;

        case "fetch":
          promise = fetch(resource.url, {
            referrerPolicy: "no-referrer",
          })
            .then((response) => response.json())
            .then((data) => {
              this.resources.get(id).element = data;
              this.resources.get(id).loaded = true;
              this.loadedCount++;
              this.completedCount++;
              this._notifyProgressListeners();
            })
            .catch(() => {
              this.resources.get(id).loaded = false;
              this.completedCount++;
              this._notifyProgressListeners();
            });
          break;
        case "file":
          promise = this.loadFile(resource.url)
            .then(() => {
              this.resources.get(id).loaded = true;
              this.loadedCount++;
              this.completedCount++;
              this._notifyProgressListeners();
            })
            .catch(() => {
              this.resources.get(id).loaded = false;
              this.completedCount++;
              this._notifyProgressListeners();
            });
          break;
        default:
          // 未知资源类型，直接标记为完成
          this.completedCount++;
          this._notifyProgressListeners();
          promise = Promise.resolve();
      }

      if (promise) promises.push(promise);
    }

    // 如果没有 promise，直接返回
    if (promises.length === 0) {
      this.loadingPromise = Promise.resolve();
      return this.loadingPromise;
    }

    this.loadingPromise = Promise.allSettled(promises)
      .then(() => {
        // 最终通知，确保进度为100%
        this._notifyProgressListeners();
      })
      .catch((error) => {
        console.error("资源预加载出错:", error);
        // 出错时也通知
        this._notifyProgressListeners();
      });

    return this.loadingPromise;
  }

  async fetchAndRegisterResources(apiUrl) {
    try {
      const response = await fetch(apiUrl, {
        referrerPolicy: "no-referrer",
      });

      if (!response.ok) {
        throw new Error(`API 请求失败: ${response.status}`);
      }

      const resourceList = await response.json();

      // 验证并格式化资源列表
      const formattedResources = this._validateAndFormatResources(resourceList);

      // 注册资源
      this.registerResources(formattedResources);

      return formattedResources;
    } catch (error) {
      console.error("从 API 获取资源失败:", error);
      // 返回空数组，避免阻塞应用
      return [];
    }
  }

  // 验证和格式化资源数据
  _validateAndFormatResources(resourceList) {
    if (!Array.isArray(resourceList)) {
      console.warn("API 返回的资源列表不是数组格式");
      return [];
    }

    return resourceList.map((item, index) => {
      return {
        id: item.id || `api_resource_${index}`,
        type: item.type || "image",
        url: item.url,
        name: item.name || item.id || `资源 ${index + 1}`,
        metadata: item.metadata || {},
      };
    });
  }

  // 获取所有图片资源（过滤并返回）
  getAllImages() {
    const images = [];

    for (const [id, resource] of this.resources) {
      if (resource.type === "image") {
        images.push({
          id: resource.id,
          url: resource.url,
          loaded: resource.loaded,
          element: resource.element, // 加载成功的 Image 对象
          metadata: resource.metadata || {},
        });
      }
    }

    return images;
  }

  // 获取特定类型的资源
  getResourcesByType(type) {
    const resources = [];

    for (const [id, resource] of this.resources) {
      if (resource.type === type) {
        resources.push({
          id: resource.id,
          url: resource.url,
          loaded: resource.loaded,
          element: resource.element,
          metadata: resource.metadata || {},
        });
      }
    }

    return resources;
  }

  // 获取资源
  getResource(id) {
    return this.resources.get(id);
  }

  // 检查是否所有资源都已加载完成
  isAllLoaded() {
    return this.completedCount >= this.totalCount;
  }

  // 获取加载进度
  getProgress() {
    if (this.totalCount === 0) return 100;
    const progress = Math.min(
      (this.completedCount / this.totalCount) * 100,
      100,
    );
    return Math.round(progress);
  }

    // 注册画廊图片资源
  registerGalleryImages(galleryPictures) {
    if (!galleryPictures || !Array.isArray(galleryPictures)) {
      console.warn("画廊图片列表无效");
      return;
    }

    // 为每张画廊图片创建资源记录
    galleryPictures.forEach((picture, index) => {
      const resourceId = `gallery_${picture.id || index}`;
      
      // 如果已存在相同ID的资源，则跳过
      if (this.resources.has(resourceId)) {
        return;
      }

      this.resources.set(resourceId, {
        id: resourceId,
        type: "image",
        url: picture.url,
        loaded: false,
        element: null,
        metadata: {
          originalData: picture, // 保存原始数据
          description: picture.description,
          time: picture.time,
          isGalleryImage: true
        }
      });
    });

    // 更新总数量
    this.totalCount = this.resources.size;
  }

  // 获取所有画廊图片资源
  getAllGalleryImages() {
    const galleryImages = [];
    
    for (const [id, resource] of this.resources) {
      if (resource.metadata?.isGalleryImage) {
        galleryImages.push({
          ...resource.metadata.originalData,
          url: resource.url,
          loaded: resource.loaded,
          resourceId: id
        });
      }
    }
    
    return galleryImages;
  }

  // 批量预加载画廊图片
  async preloadGalleryImages(galleryPictures) {
    if (!galleryPictures || galleryPictures.length === 0) {
      return Promise.resolve();
    }

    // 注册画廊图片资源
    this.registerGalleryImages(galleryPictures);

    // 过滤出需要加载的画廊图片
    const galleryResourceIds = [];
    for (const [id, resource] of this.resources) {
      if (resource.metadata?.isGalleryImage && !resource.loaded) {
        galleryResourceIds.push(id);
      }
    }

    if (galleryResourceIds.length === 0) {
      return Promise.resolve();
    }

    // 创建加载任务
    const promises = galleryResourceIds.map(id => {
      const resource = this.resources.get(id);
      if (resource.type === "image") {
        return this.loadImage(resource.url)
          .then((element) => {
            this.resources.get(id).element = element;
            this.resources.get(id).loaded = true;
            this.loadedCount++;
            this.completedCount++;
            this._notifyProgressListeners();
          })
          .catch(() => {
            this.resources.get(id).loaded = false;
            this.completedCount++;
            this._notifyProgressListeners();
          });
      }
      return Promise.resolve();
    });

    return Promise.allSettled(promises);
  }

  // 清空调色板图片资源（可选）
  clearGalleryImages() {
    const toDelete = [];
    for (const [id, resource] of this.resources) {
      if (resource.metadata?.isGalleryImage) {
        toDelete.push(id);
      }
    }
    
    toDelete.forEach(id => {
      this.resources.delete(id);
    });
    
    // 重新计算计数
    this.loadedCount = Array.from(this.resources.values()).filter(r => r.loaded).length;
    this.completedCount = Array.from(this.resources.values()).length;
    this.totalCount = this.resources.size;
  }

}

function getHbgUrl() {
  const now = new Date().getHours();
  if (now >= 8 && now <= 16) {
    return "2";
  }
  if ((now > 16 && now <= 18) || (now > 5 && now <= 7)) {
    return "2";
  }
  return "1";
}

export const resourceLoader = new ResourceLoader();

// 资源列表
export const defaultResources = [
  {
    id: "hbg",
    type: "image",
    url: `/hbg${getHbgUrl()}.webp`,
  },
  {
    id: "hbgbackup",
    type: "image",
    url: "/hbgc.png",
  },
  {
    id: "treemodel",
    type: "file",
    url: "/e.obj",
  },
  {
    id: "3d.sky.stars_bottom",
    type: "image",
    url: "/textures/sky/stars_bottom.png",
  },
  {
    id: "3d.sky.stars_top",
    type: "image",
    url: "/textures/sky/stars_top.png",
  },
  {
    id: "3d.sky.stars_west",
    type: "image",
    url: "/textures/sky/stars_west.png",
  },
  {
    id: "3d.sky.stars_east",
    type: "image",
    url: "/textures/sky/stars_east.png",
  },
  {
    id: "3d.sky.stars_north",
    type: "image",
    url: "/textures/sky/stars_north.png",
  },
  {
    id: "3d.sky.stars_south",
    type: "image",
    url: "/textures/sky/stars_south.png",
  },
  {
    id:"background2",
    type:"image",
    url:"https://cdn.jsdmirror.com/gh/ororigin/tdy_website_cdn/backgrounds/background2.png"
  }
];
